import React, { useMemo } from 'react';
import BaseTable from 'antd/es/table';
import Tooltip from 'antd/es/tooltip';

import { Space } from './layout';
import { Text, Link } from './Text';
import Icon from './Icon';

import { dateTimeFormat } from 'config/common';
import { formatDate, formatDuration } from 'helpers/date';

const Table = ({ actions, actionsProps, onAction, columns, children, ...props }) => {
  const allColumns = useMemo(() => {
    let allColumns = [];

    if (Array.isArray(columns)) {
      allColumns = allColumns.concat(columns.map(props => {
        return Column.fromProps(Object.assign({}, props));
      }));
    }

    const childrenColumns = React.Children.map(children, child => {
      if (child && child.type.prototype === Column.prototype) {
        const props = Object.assign({}, child.props);
        return Column.fromProps(props);
      }
      return null;
    });

    if (childrenColumns) {
      allColumns = allColumns.concat(childrenColumns.filter(column => column !== null));
    }

    if (actions && actions.length) {
      allColumns.push({
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: item => <ActionsColumn
          currentItem={item}
          actions={actions}
          onAction={onAction}
          {...actionsProps}
        />
      });
    }

    return allColumns;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, actionsProps, onAction]);

  return (
    <BaseTable columns={allColumns} {...props} />
  );
};

const ActionsColumn = ({ actions, currentItem, onAction, ...rest }) => {
  const handleAction = (actionType) => {
    return (e) => {
      if (typeof onAction === 'function')
        onAction(actionType, currentItem);
    };
  };

  return (
    <Space size="small">
      {actions.map((Action, i) =>
        <Action key={i} handleAction={handleAction} item={currentItem} {...rest} />
      )}
    </Space>
  )
};

const Column = () => {
  return null;
};

Column.fromProps = ({ type = 'text', tooltip = null, icon = null, children, ...props }) => {
  const columnType = columnTypes[type];
  let columnRenderer = props.render;

  if (!columnRenderer) {
    if (!columnType) {
      throw new Error(`Invalid column type ${type}`);
    }
    columnRenderer = (value, item, index) => {
      return columnType({ ...props, value, item, index });
    };
  }

  if (icon) {
    if (typeof icon === 'function') {
      icon = icon();
    } else if (typeof icon === 'object') {
      icon = (<Icon {...icon} />)
    }
  } else if (tooltip) {
    if (typeof tooltip === 'function') {
      tooltip = tooltip();
    } else if (typeof tooltip === 'object') {
      tooltip = (
        <Tooltip overlayStyle={{ maxWidth: 'unset' }} {...tooltip}>
          <span style={{ marginBottom: '-2px', marginLeft: '6px' }}>
            <Icon
              name="info"
              size={16}
              strokeWidth={'3'}
              color={'#A4B0BA'}
            />
          </span>
        </Tooltip>
      );
    } else if (typeof tooltip === 'string') {
      tooltip = (
        <Tooltip overlayStyle={{ maxWidth: 'unset' }} title={tooltip} trigger="hover" placement="top">
          <span style={{ marginBottom: '-2px', marginLeft: '6px' }}>
            <Icon
              name="info"
              size={16}
              strokeWidth={'3'}
              color={'#A4B0BA'}
            />
          </span>
        </Tooltip>
      );
    }
  }

  if (tooltip || icon) {
    props.title = (
      <span style={{ height: '100%', display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        {props.title}
        {icon}
        {tooltip}
      </span>
    );
  }

  return {
    ...props,
    ...(children ? {
      children: children.map(props => Column.fromProps(Object.assign({}, props)))
    } : {}),
    render: columnRenderer,
  };
};

const columnTypes = {
  text: ({ value, noValue = '-', prefix = '', suffix = '', item, onClick, styles = {} }) => {
    if (value !== undefined && value !== null) value = value.toString();
    if (typeof value !== 'string') return noValue;

    const onClickHandler = (e) => {
      if (typeof onClick === 'function') {
        onClick(e, value, item);
      }
    };

    return (
      <Text style={styles} onClick={onClickHandler}>
        {`${prefix}${value}${suffix}`}
      </Text>
    );
  },

  number: ({ value, precision, rounded, readable, ...props }) => {
    let val;
    if (typeof value === 'number') {
      if (typeof precision === 'number') {
        val = parseFloat(value.toFixed(precision));
      } else if (rounded) {
        val = Math.round(value);
      } else {
        val = value;
      }
      if (readable) {
        val = new Intl.NumberFormat('en-US').format(val);
      } else {
        val = val.toString();
      }
    }
    return columnTypes.text({ value: val, ...props });
  },

  datetime: ({ value, format = dateTimeFormat, tzOffset, item, ...props }) => {
    let val;
    if (typeof tzOffset === 'string') {
      tzOffset = Number(item[tzOffset]);
    }
    if (typeof value === 'number') {
      if (value.toString().length === 10) value *= 1000;
      val = formatDate(value, format, { tzOffset });
    } else if (typeof value === 'string') {
      const dt = new Date(value);
      if (!isNaN(dt.getTime())) {
        val = formatDate(dt.getTime(), format, { tzOffset });
      }
    }
    return columnTypes.text({ value: val, item, ...props });
  },

  duration: ({ value, inHrs, inMins, withSecs, ...props }) => {
    let val;
    if (typeof value === 'number') {
      if (inHrs) {
        val = formatDuration(value * 3600 * 1000, withSecs);
      } else if (inMins) {
        val = formatDuration(value * 60 * 1000, withSecs);
      } else {
        val = formatDuration(value * 1000, withSecs);
      }
    }
    return columnTypes.text({ value: val, ...props });
  },

  percentage: ({ value, ...props }) => {
    let val;
    if (typeof value !== 'number') {
      return columnTypes.text({ ...props });
    } else {
      val = value * 100;
    }
    return columnTypes.number({ value: val, suffix: '%', ...props });
  },

  link: ({ value, to, prefix = '', suffix = '', ...props }) => {
    if (typeof to === 'function') {
      to = to({ value, ...props });
    }
    if (typeof to !== 'object') {
      if (typeof to !== 'string') {
        return columnTypes.text({ value, ...props });
      }
      to = {
        pathname: to,
        state: {
          ...props.item,
        }
      };
    }

    if (!value) {
      return columnTypes.text({ value, ...props });
    }

    return (
      <Link to={to}>
        {`${prefix}${value}${suffix}`}
      </Link>
    );
  }
};

export default Table;
Table.Column = Column;
Column.columnTypes = columnTypes;
